/**
 * 티켓에 무슨 일이 있었나 — **창업 문장의 나머지 절반이다.**
 *
 * 「AI Agent 로 개발하는데 티켓이 연동되지 않아 히스토리를 못 찾겠다」에서
 * 코드 쪽(PR·커밋·웹훅)은 2026-09-02 에 닫았는데, **티켓 자체의 역사는
 * 어디에도 안 남고 있었다** — 우선순위를 누가 언제 낮췄는지, 작업자가 언제
 * 바뀌었는지가 전부 사라지고 마지막 값 하나만 남았다.
 *
 * ## 값을 옮기는 일은 화면이 한다
 *
 * 서버는 코드를 **코드 그대로** 준다(`todo`·`high`). 화면이 그 말을 아는
 * 표를 이미 갖고 있어서다(`columns.ts`) — 서버가
 * 한국어로 옮기면 같은 표가 두 벌이 되고, **한쪽만 늘어나는 날 조용히
 * 어긋난다.** 그래서 여기서는 표를 새로 만들지 않고 그 표를 찾아 읽기만 한다.
 *
 * ## 조사를 값에 안 붙인다
 *
 * 알림 종(`NotificationBell` 의 `noticeText`)이 같은 함정을 이미 밟았고 거기
 * 적힌 이유가 여기 그대로 적용된다 — 한국어 조사(가/이 · 으로/로)가 앞 글자의
 * 끝소리에 따라 갈려서 **값을 문장에 끼우면 절반의 값에서 문장이 어색해진다.**
 * 이 줄이 다루는 값은 「이슈」·「Todo」·「staging」·「완료」처럼 받침이 섞여
 * 있어 `으로`/`로` 를 한 줄로 못 쓴다.
 *
 * 그래서 **문장을 만들지 않고 「이름표 · 옛값 → 새값」 꼴로 적는다.** 조사가
 * 붙는 자리가 아예 없으니 어긋날 자리도 없다. 사람 이름(주어)도 같은 이유로
 * 줄 안이 아니라 **묶음 머리에 따로 선다.**
 *
 * ## 한 번의 수정은 한 묶음이다
 *
 * `update_ticket` 하나가 제목·우선순위·작업자를 함께 만지면 줄이 셋 나온다.
 * 셋을 각각 세우면 **같은 사람이 같은 시각에 세 번 일한 것처럼 읽힌다** —
 * 이름과 시각이 세 번 반복되고, 정작 「이 사람이 한 번에 무엇을 바꿨나」는
 * 눈으로 다시 이어 붙여야 한다.
 *
 * 서버가 한 트랜잭션의 줄에 **같은 `created_at` 을 찍는다**(그래서 정렬을
 * `created_at` 이 아니라 `id` 로 한다 — 라우터가 그 이유를 적어 뒀다).
 * 그 값이 곧 「한 번의 수정」이라, **잇달아 오면서 시각과 사람이 같은 줄을**
 * 한 묶음으로 세운다. 사람까지 보는 것은 값이 싸서다 — 두 사람이 같은 밀리초에
 * 저장하는 일은 드물지만, 그때 한 묶음이 되면 **한 일을 남에게 돌린다.**
 * 잇달아 온 것만 묶으므로 **일어난 차례는 어떤 경우에도 안 흐트러진다.**
 *
 * ## 없으면 절째로 안 그린다
 *
 * 갓 만든 티켓은 역사가 없다. PR·커밋 절은 비었을 때 브랜치 규칙을 가르치므로
 * 빈 채로 서 있을 값이 있지만, **여기는 빈 상태가 가르칠 것이 없다** — 모든
 * 새 티켓에 「아직 없다」 한 줄이 붙어 화면만 길어진다.
 */

import { useEffect, useState } from 'react';

import {
  errorMessage,
  listTicketEvents,
  NotLoggedInError,
  type TicketEvent,
  type TicketEventField,
} from '../api';
import { BADGE } from '../board/Chips';
import { COLUMNS, PRIORITIES, TYPES } from '../columns';
import { formatWhen } from '../format';
import { SectionTitle } from './section';

/**
 * 칸 이름표. **티켓 상세가 그 칸을 부르는 이름을 그대로 쓴다** — 위에서는
 * 「상태」인데 여기서만 「칸」이면 같은 것을 두 이름으로 배우게 된다.
 *
 * ⚠ `type` 이 「작업 유형」인 것은 「유형」이 이미 다른 축의 이름이라서다
 * (`kind` — 할 일/이슈, 만들 때 정하고 안 바뀐다).
 */
const FIELD_LABEL: Record<TicketEventField, string> = {
  qa: 'QA 검증',
  requires_deploy: '배포 필요',
  col: '상태',
  title: '제목',
  body: '설명',
  type: '작업 유형',
  priority: '우선순위',
  assignee: '작업자',
  due_date: '마감',
  estimate_days: '소요 예정',
  held: '보류',
  deploy_col: '배포',
  project: '프로젝트',
};

/**
 * 옛 배포 칸 이름표. **배포 기능은 2026-10-18 에 걷었다** — 그 전에 쌓인
 * 활동 기록을 읽을 때만 쓴다. 새 기록에는 이 칸이 안 생긴다.
 */
const DEPLOY_COLUMNS = [
  { id: 'wait', label: '대기' },
  { id: 'staging', label: '스테이징' },
  { id: 'production', label: '프로덕션' },
  { id: 'failed', label: '실패' },
] as const;

/** 값이 비어 있던 자리. 화살표 왼쪽이 비면 「무엇에서」가 사라진다. */
const NOTHING = '없음';

/**
 * 코드를 사람 말로. **표는 `columns.ts` 쪽이 정본이고 여기서는 읽기만 한다.**
 *
 * `columnLabel`·`typeOf`·`priorityOf` 를 직접 못 부르는 것은 그것들이 코드
 * 유니언(`TicketCol` 등)을 받는데 **변경 줄이 들고 오는 값은 그냥 글자**이기
 * 때문이다. 좁히려고 값을 다시 나열하면 그 나열이 곧 두 번째 표가 된다 —
 * 그래서 표 자체를 찾는다.
 *
 * 못 찾으면 **코드를 그대로 보여 준다.** 서버가 값을 늘렸을 때 빈칸을 그리면
 * 「무엇이 바뀌었나」가 통째로 사라진다 — 낯선 코드라도 남는 편이 낫다.
 */
function labelIn(
  table: readonly { id: string; label: string }[],
  code: string,
): string {
  return table.find((row) => row.id === code)?.label ?? code;
}

/** 한쪽 값 하나를 글자로. `null` 은 「없음」이다. */
function valueText(field: TicketEventField, value: string | null): string {
  if (field === 'requires_deploy') return value === 'True' ? '필요' : '불필요';
  if (field === 'qa' && value) {
    try {
      const qa = JSON.parse(value) as Record<string, unknown>;
      const statuses: Record<string, string> = {pending: '대기', passed: '통과', failed: '실패', not_required: '불필요'};
      return [typeof qa.status === 'string' ? statuses[qa.status] : '', qa.version, qa.targets, qa.evidence]
        .filter((item): item is string => typeof item === 'string' && Boolean(item)).join(' · ') || '미지정';
    }
    catch { return value; }
  }
  if (value === null) return NOTHING;
  switch (field) {
    case 'col':
      return labelIn(COLUMNS, value);
    case 'deploy_col':
      return labelIn(DEPLOY_COLUMNS, value);
    case 'type':
      return labelIn(TYPES, value);
    case 'priority':
      // ⚠ **화살표 표식(`mark`)을 안 붙인다.** 「보통」의 표식이 `→` 라,
      //    붙이면 옛값·새값을 가르는 화살표와 글자가 같아진다.
      return labelIn(PRIORITIES, value);
    case 'estimate_days':
      return `${value}일`;
    case 'project':
      // 서버가 프로젝트 **번호**를 담는다(`ticket_events.snapshot` — 관계를
      // 만지면 웹훅 경로에서 죽는다고 그 자리에 적혀 있다). 이름을 모르니
      // 지어내지 않고 번호로 적는다.
      return `#${value}`;
    default:
      return value;
  }
}

/**
 * 줄 하나를 이름표와 글자로. **문장이 아니라 「이름표 · 값」이다**(머리말의
 * 조사 이야기).
 */
export function eventLine(event: TicketEvent): { label: string; text: string } {
  const label = FIELD_LABEL[event.field] ?? event.field;
  // 본문은 옛 글을 안 담는다 — 담으면 이 표가 티켓보다 커진다(모델 머리말).
  // 그래서 화면도 「바뀌었다」까지만 말할 수 있다.
  if (event.field === 'body') return { label, text: '고쳤다' };
  // 보류는 값이 `'held'` 아니면 `null` 이라 화살표로 적으면
  // 「없음 → held」가 된다. 사람이 한 일 그대로 적는다.
  if (event.field === 'held') {
    return { label, text: event.to_text === null ? '되돌렸다' : '미뤘다' };
  }
  const from = valueText(event.field, event.from_text);
  const to = valueText(event.field, event.to_text);
  return { label, text: `${from} → ${to}` };
}

type Group = {
  key: string;
  actorName: string | null;
  createdAt: string;
  events: TicketEvent[];
};

/**
 * 한 번의 수정을 한 묶음으로. **잇달아 오면서 시각과 사람이 같은 것만**
 * 묶으므로 일어난 차례가 안 흐트러진다(머리말).
 */
export function groupEvents(events: TicketEvent[]): Group[] {
  const groups: Group[] = [];
  for (const event of events) {
    const last = groups.at(-1);
    if (
      last !== undefined &&
      last.createdAt === event.created_at &&
      last.actorName === event.actor_name
    ) {
      last.events.push(event);
      continue;
    }
    groups.push({
      key: String(event.id),
      actorName: event.actor_name,
      createdAt: event.created_at,
      events: [event],
    });
  }
  return groups;
}

export function Activity({
  ticketId,
  rail,
  onUnauthorized,
}: {
  ticketId: number;
  /** 인용선 색. 상세의 다른 절들과 같은 값을 받는다 — 같은 레일 위에
   *  서야 한 층위로 읽힌다. */
  rail: string;
  onUnauthorized: () => void;
}) {
  /** `null` 은 「아직 못 읽었다」다 — 아무 일도 없었던 것(`[]`)과 다르다. */
  const [events, setEvents] = useState<TicketEvent[] | null>(null);
  /**
   * 읽기가 깨졌다. **`role="alert"` 를 안 단다** — 사람이 방금 한 일이
   * 실패한 것이 아니라 절 하나가 못 채워진 것이고, 이 모달에는 이미 외치는
   * 자리가 둘 더 있다(설명칸 붙여넣기 · 댓글). `GithubPanel` 과 같은 규칙이다.
   */
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const got = await listTicketEvents(ticketId);
        if (cancelled) return;
        setEvents(got.events);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) {
          onUnauthorized();
          return;
        }
        setError(`바뀐 것을 불러오지 못했다: ${errorMessage(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketId, onUnauthorized]);

  // 못 읽었거나(읽는 중) 아무 일도 없었으면 **절째로 안 그린다**(머리말).
  // 실패는 그렇게 삼키지 않는다 — 못 채운 절이 조용히 사라지면 「역사가
  // 없다」와 「역사를 못 읽었다」가 화면에서 같아진다.
  if (error === null && (events === null || events.length === 0)) return null;

  const groups = events === null ? [] : groupEvents(events);

  return (
    <section
      aria-label="바뀐 것"
      className="border-l-[6px] pl-s4"
      style={{ borderColor: rail }}
    >
      <SectionTitle>
        바뀐 것
        {events !== null && events.length > 0 && (
          <span className="tnum"> {events.length}</span>
        )}
      </SectionTitle>

      {error && <p className="m-0 text-meta text-st-red-f">{error}</p>}

      <ul className="m-0 flex list-none flex-col gap-s4 p-0">
        {groups.map((group) => (
          <li key={group.key}>
            <div className="flex flex-wrap items-center gap-s2">
              {group.actorName === null ? (
                /* **사람이 아니다.** 웹훅이 PR 병합을 보고 옮긴 것이라
                   서버가 이름을 안 남긴다. 배지로 세우는 것은 댓글의
                   「AI」와 같은 규칙 — 기계가 한 일은 그 사실이 남아야 한다.
                   이름 자리에 「알 수 없음」을 적으면 **사람인데 계정이
                   사라진 것**과 구별이 안 된다. */
                <span className={`${BADGE} bg-sunk font-bold text-ink-2`}>
                  GitHub
                </span>
              ) : (
                <span className="text-meta text-ink">{group.actorName}</span>
              )}
              <span className="tnum text-micro text-ink-3">
                {/* 옆 절(댓글·커밋)이 절대 시각이라 여기만 「5분 전」이면
                    둘을 시간순으로 못 맞춘다. 같은 화면에서는 같은 자를 쓴다. */}
                {formatWhen(group.createdAt)}
              </span>
            </div>

            <ul className="m-0 mt-s2 flex list-none flex-col gap-s1 p-0">
              {group.events.map((event) => {
                const line = eventLine(event);
                return (
                  <li
                    key={event.id}
                    className="flex flex-wrap items-baseline gap-s2"
                  >
                    {/* **이름표지 소제목이 아니다.** 값 하나를 가리키는
                        말이라 굵게 세우지 않는다 — 가르는 일은 색과 크기가
                        한다(`GithubPanel` 의 저장소 이름과 같은 규칙). */}
                    <span className="text-micro text-ink-3">{line.label}</span>
                    <span className="tnum text-meta break-keep text-ink">
                      {line.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}
